import { Box, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from "@mui/material"
import { useEffect, useState } from "react"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { PaginationPortal } from "lib/@components/pagination-portal"
import { TabledRepeatHeaderRow } from "lib/@components/tabled-repeat/tabled-repeat-header-row"

/**
 * RequestTabledRepeat renders a table whose rows are supplied one server page at a time.
 * RequestPageRepeat owns the page navigation while the table header and row layout remain
 * consistent with TabledRepeat.
 */
export function RequestTabledRepeat({
    items = [],
    total = 0,
    setSkip,
    pageSize = 10,
    refreshKey = "",
    tableHeaders,
    itemPass = "item",
    itemComponent,
    fallback,
    emptyMessage = "No results",
    alternating = true,
    sx = {},
    headerStyles = {},
    requestPageRepeatProps = {},
}) {
    const initialFieldKey = tableHeaders.find((tableHeader) => tableHeader.sortable)?.fieldKey ?? ""
    const [sortedItems, setSortedItems] = useState(items)
    const [order, setOrder] = useState("")
    const [orderBy, setOrderBy] = useState(initialFieldKey)

    useEffect(resetTable, [items, initialFieldKey])

    const emptyFallback = fallback ?? (
        <TableRow>
            <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                </Typography>
            </TableCell>
        </TableRow>
    )

    const rowStyles = alternating
        ? {
              "tr:nth-of-type(even) td": {
                  backgroundColor: "action.hover",
              },
              "tr:nth-of-type(odd) td": {
                  backgroundColor: "background.paper",
              },
          }
        : {
              "tr:not(:last-of-type) td": {
                  borderBottom: "0.75px solid #ebedef",
              },
          }

    return (
        <Box>
            <TableContainer>
                <Table sx={{ ...rowStyles, ...sx }}>
                    <TabledRepeatHeaderRow
                        tableHeaders={tableHeaders}
                        orderBy={orderBy}
                        order={order}
                        sortedItems={sortedItems}
                        setSortedItems={setSortedItems}
                        setOrder={setOrder}
                        setOrderBy={setOrderBy}
                        initialFieldKey={initialFieldKey}
                        sx={headerStyles}
                    />
                    <RequestPageRepeat
                        {...requestPageRepeatProps}
                        list={sortedItems}
                        total={total}
                        setSkip={setSkip}
                        pageSize={pageSize}
                        refreshKey={refreshKey}
                        portal
                        Component={TableBody}
                        item={itemComponent}
                        pass={itemPass}
                        fallback={emptyFallback}
                    />
                </Table>
            </TableContainer>
            <PaginationPortal />
        </Box>
    )

    function resetTable() {
        setSortedItems(items)
        setOrderBy(initialFieldKey)
        setOrder("")
    }
}
